import { defineStore } from "pinia";
import { shallowRef } from 'vue';
import { DateTime } from 'luxon';

export const useRequestStore = defineStore("request", {
  state: () => ({
    actionStatus: "ST_STARTRQST",
    isArchived: false,
    aspect: "",
    bottomElevation: 0,
    currentUpdate: 0,
    coordinates: [0, 0],
    id: 0,
    initialCoordinates: [-100, 41],
    deliverAsap: false,
    deliverAt: shallowRef({}), // prevent vue tracking luxon properties
    draftForecast: "",
    drainage: "",
    fireZoneSiteName: "",
    fireZoneOfficeId: "",
    fireZoneSiteId: "LBF",
    fuelType: "",
    futureForecastSchedules: [],
    incident: {},
    incidentPFReason: "",
    isDaylightSavings: true,
    isEditing: false,
    isHysplit: "No",
    observations: [],
    office: "",
    projectName: "",
    quad: "",
    question: "",
    reconcileWith: "",
    remarks: "",
    requestElements: [],
    requestFormats: [],
    requesterAgency: "",
    requesterEmail: "",
    additionalEmails:[],
    requesterFax: "",
    requesterMeta: navigator.userAgent,
    requesterName: "",
    requesterOfficial: "",
    requesterPhone: "",
    requesterPhoneExt: "",
    sheltering: "",
    siteId: "",
    siteName: "",
    size: 0,
    spotForecasts: [],
    startedAt: shallowRef({}), // prevent vue tracking luxon properties
    state: "",
    submittedAt: shallowRef({}), // prevent vue tracking luxon properties,
    Timezone: {},
    TimezonesSupported: [],
    topElevation: 0,
    allowOutlookForecast:false,
    outlookForecast: 0,
    allowStartTimeForecast:false,
    startTimeForecast: false,
    viewableIncidents: [],
    zoom: 4.5,
    '@id': 0,
    '@type': "",
  }),
  persist: {
    storage: sessionStorage,
    afterRestore: (ctx) => {
      if (ctx.store?.startedAt?.length > 0) {
        ctx.store.startedAt = DateTime.fromISO(ctx.store.startedAt, { zone: 'UTC' });
        console.log('Rehydrating startedAt', ctx.store.startedAt.toISO())
      }
      if (ctx.store?.deliverAt?.length > 0) {
        ctx.store.deliverAt = DateTime.fromISO(ctx.store.deliverAt, { zone: 'UTC' });
        console.log('Rehydrating deliverAt', ctx.store.deliverAt.toISO())
      }
    },
    paths: [
      "aspect",
      "bottomElevation",
      "coordinates",
      "deliverAt",
      "drainage",
      "fireZoneSiteId",
      "fireZoneSiteName",
      "fuelType",
      "futureForecastSchedules",
      "id",
      "incident",
      "incidentPFReason",
      "isEditing",
      "isHysplit",
      "latitude",
      "longitude",
      "observations",
      "office",
      "projectName",
      "quad",
      "question",
      "reconcileWith",
      "remarks",
      "requestElements",
      "requestFormats",
      "requesterAgency",
      "requesterOfficial",
      "requesterEmail",
      "requesterPhone",
      "requesterPhoneExt",
      "requesterFax",
      "requesterName",
      "siteId",
      "siteName",
      "sheltering",
      "size",
      "startedAt",
      "state",
      "Timezone",
      "TimezonesSupported",
      "topElevation",
      "allowOutlookForecast",
      "outlookForecast",
      "allowStartTimeForecast",
      "startTimeForecast",
      "@id",
      "@type",
    ],
  },
getters: {
    longitude() {
      return Number(this.coordinates[0].toFixed(4));
    },
    latitude() {
      return Number(this.coordinates[1].toFixed(4));
    },
    elevation() {
      return this.topElevation;
    },
    newRequestData() {
      let newRequest = this.createBaseRequest();
      newRequest.Timezone = this.Timezone.id;
      newRequest.incident = this.incident.id;

      // ID can not be sent for a new request.
      delete newRequest.id;

      return newRequest;
    },
    editRequestData() {
      let editRequest = this.createBaseRequest();
      editRequest.incident = this.incident["@id"];
      editRequest.Timezone = this.Timezone["@id"];
      editRequest.office = this.office["@id"];
      return editRequest
    },
    startedAtInLocalTime() {
      return this.startedAt.setZone(this.Timezone.IANA);
    },
    deliverAtInLocalTime() {
      return this.deliverAt.setZone(this.Timezone.IANA);
    }
  },
  actions: {
    setSiteId(id) {
      this.siteId= id;
    },
    setSiteName(name) {
      this.siteName = name;
    },
    setIsEditing(editing) {
      this.isEditing = editing;
    },
    setIncidentId(id) {
      console.debug(`setting id ${id}`);
      this.incidentId = `/incidents/${id}`;
    },
    setCoordinates(longitude, latitude) {
      this.coordinates[0] = longitude;
      this.coordinates[1] = latitude;
    },
    setTimezone(timezone) {
      this.Timezone = timezone;
    },
    setTimezonesSupported(timezonesSupported) {
      timezonesSupported.forEach((timezone) => {
        this.TimezonesSupported.push(timezone);
      });
    },
    setEmails(emails){
      this.additionalEmails = emails.split(",");
      this.requesterEmail = "";
    },
    setOutlookForecast(isAllow, value){
      this.allowOutlookForecast = isAllow;
      this.outlookForecast = value;
    },
    setStartTimeForecast(isAllow, value){
      this.allowStartTimeForecast = isAllow;
      this.startTimeForecast = value;
    },
    createBaseRequest() {
      let newRequest = Object.assign({}, this.$state);

      // Concat emails into a string
      for (let i = newRequest.additionalEmails.length - 1; i >= 0; i--) {
        if (newRequest.additionalEmails[i] === undefined ||
            newRequest.additionalEmails[i] === null ||
            newRequest.additionalEmails[i] === ""
          ) {
          newRequest.additionalEmails.splice(i, 1);
        }
      }
      newRequest.requesterEmail = newRequest.additionalEmails.join(",");

      //Set longitude & latitude fields for request JSON payload
      newRequest.longitude = this.longitude;
      newRequest.latitude = this.latitude;

      newRequest.deliverAt = this.deliverAt.toISO();
      newRequest.startedAt = this.startedAt.toISO();

      // Outlook flag to Int
      newRequest.outlookForecast = parseInt(this.outlookForecast);

      // Start Time for the Forecast
      newRequest.startTimeForecast = this.startTimeForecast;

      // Remove data not needed for the request.
      delete newRequest.submittedAt; // the API defines this
      delete newRequest.viewableIncidents;
      delete newRequest.initialCoordinates;
      delete newRequest.coordinates;
      delete newRequest.draftForecast;
      delete newRequest.isEditing;
      delete newRequest.zoom;
      delete newRequest.fireZoneOfficeId;
      delete newRequest.fireZoneSiteId;
      delete newRequest.fireZoneSiteName;
      delete newRequest.TimezonesSupported;
      // Remove these deletes once notification feature is fully implemented
      delete newRequest.notificationState;
      delete newRequest.reminderNotificationTime;
      delete newRequest.initialNotificationTime;
      delete newRequest.allowOutlookForecast;
      delete newRequest.allowStartTimeForecast;

      // Only send reconcileWith to the API when a valid tag is set
      if (!newRequest.reconcileWith) {
        delete newRequest.reconcileWith;
      }

      return newRequest;
    },
  },
});
